import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ceap_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Cache-busting para evitar respostas em cache do navegador em rotas críticas
  if (config.url && (config.url.includes("/auth/") || config.url.includes("/wallet"))) {
    config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    config.headers["Pragma"] = "no-cache";
    config.headers["Expires"] = "0";
  }
  return config;
});

// Endpoints que foram removidos mas builds antigos do frontend ainda podem chamar.
// Para esses, retornamos respostas vazias ao invés de propagar o 404.
const DEPRECATED_ENDPOINTS = [
  "/auth/test-credentials",
  "/auth/magic-login",
  "/auth/restore-test-credentials",
];

// Função para esperar o backend "acordar" (handles preview hibernation).
async function waitForBackendWake(maxAttempts = 8, intervalMs = 2000) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await axios.get(`${API}/health`, { timeout: 8000 });
      if (res?.data?.status === "healthy") return true;
    } catch {
      /* ainda dormindo */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}

// Wrapper de retry para chamadas críticas (login, wallet, scan) que podem falhar
// com 404/Network Error quando o backend está hibernando no ambiente de preview.
async function retryRequest(originalConfig, maxAttempts = 4) {
  // Tentar acordar o backend antes de re-tentar a requisição
  const woke = await waitForBackendWake(6, 2500);
  if (!woke) {
    throw new Error("Backend não respondeu. Tente novamente em alguns instantes.");
  }
  // Replay da requisição original
  return axios.request({
    ...originalConfig,
    baseURL: undefined, // já está absoluto
    url: originalConfig.baseURL ? originalConfig.baseURL + originalConfig.url : originalConfig.url,
  });
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error?.config || {};
    const url = config.url || "";
    const status = error?.response?.status;

    // Silenciar 404 de endpoints já removidos (build em cache antigo)
    if (status === 404 && DEPRECATED_ENDPOINTS.some((ep) => url.includes(ep))) {
      console.warn(`[CEAP] Endpoint removido chamado por build antigo: ${url} — ignorando`);
      return Promise.resolve({ data: null, status: 404, _deprecated: true });
    }

    // 404 ou erro de rede em rotas que existem = backend provavelmente hibernando
    // Tenta acordar o backend e re-executar a requisição UMA vez
    const isNetworkOr404 = !error.response || status === 404 || status === 502 || status === 503 || status === 504;
    const isCriticalRoute = /\/(auth|wallet|attendance|courses|certificates|free-courses|materials|consultancies|levels|leads|notifications|admin)\b/.test(url);
    if (isNetworkOr404 && isCriticalRoute && !config._retried) {
      config._retried = true;
      console.warn(`[CEAP] Backend possivelmente hibernando (status=${status || "network"}). Tentando acordar...`);
      try {
        const woke = await waitForBackendWake(6, 2500);
        if (woke) {
          console.info("[CEAP] Backend acordou! Re-executando requisição.");
          // Re-executa com o mesmo config (axios trata cabeçalhos, body etc)
          return api.request(config);
        }
      } catch {
        /* segue para erro abaixo */
      }
    }

    if (status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register") && !path.startsWith("/validar") && !path.startsWith("/admin/login")) {
        localStorage.removeItem("ceap_token");
        localStorage.removeItem("ceap_user");
      }
    }
    return Promise.reject(error);
  }
);

// Keep-alive: pinga /api/health a cada 4 minutos para manter o backend
// "acordado" enquanto o aluno tem a aba aberta. Sem isso, no ambiente
// de preview o backend hiberna após inatividade.
if (typeof window !== "undefined" && !window.__ceapKeepAlive) {
  window.__ceapKeepAlive = setInterval(() => {
    axios.get(`${API}/health`, { timeout: 8000 }).catch(() => {});
  }, 4 * 60 * 1000); // 4 minutos
}

export default api;

export const LOGO_URL = "/logo-ceap.png";

// Helper exportado para que o app possa exibir tela "acordando servidor"
export { waitForBackendWake };
